import {
  type DocumentInfo,
  DocumentSubType,
  type FormInt,
  type FormIntQuestion,
  type Objection,
  ObjectionType,
  PropoundingParty,
  Representative,
  Respondent,
} from "@/briefpoint-client";
import {
  PETITIONER_AND_RESPONDENT_TYPES,
  PRODUCE_DOCUMENTS_OBJECTION_ID,
} from "./constants";
import { getSubTypeText } from "./utils";

export function getRespondentDisplay(
  documentInfo: DocumentInfo,
  toUpperNonCustomRespondentNames = false,
  isFamilyLaw?: boolean,
) {
  const { respondent, respondentCustom } = documentInfo;
  if (respondent === Respondent.Custom && respondentCustom) {
    return respondentCustom;
  }
  if (respondent === Respondent.PlaintiffOrDefendant) {
    return toUpperNonCustomRespondentNames
      ? getPartyDisplay(
          documentInfo.respondingParty,
          documentInfo.isCrossComplaint,
          isFamilyLaw,
        ).toUpperCase()
      : getPartyDisplay(
          documentInfo.respondingParty,
          documentInfo.isCrossComplaint,
          isFamilyLaw,
        );
  }
  return toUpperNonCustomRespondentNames
    ? (respondent === Respondent.RespondingParty
        ? "Responding Party"
        : "Respondent"
      ).toUpperCase()
    : respondent === Respondent.RespondingParty
      ? "Responding Party"
      : "Respondent";
}

export function getPartyDisplay(
  party: PropoundingParty | undefined,
  isCrossComplaint: boolean | undefined,
  isFamilyLaw?: boolean,
) {
  const propoundingPartyNameMap = new Map([
    [
      PropoundingParty.Plaintiff,
      isFamilyLaw
        ? PETITIONER_AND_RESPONDENT_TYPES.PETITIONER
        : PETITIONER_AND_RESPONDENT_TYPES.PLAINTIFF,
    ],
    [
      PropoundingParty.Defendant,
      isFamilyLaw
        ? PETITIONER_AND_RESPONDENT_TYPES.RESPONDENT
        : PETITIONER_AND_RESPONDENT_TYPES.DEFENDANT,
    ],
    [
      PropoundingParty.CrossComplainant,
      isFamilyLaw
        ? PETITIONER_AND_RESPONDENT_TYPES.CROSS_PETITIONER
        : PETITIONER_AND_RESPONDENT_TYPES.CROSS_COMPLAINANT,
    ],
    [
      PropoundingParty.CrossDefendant,
      isFamilyLaw
        ? PETITIONER_AND_RESPONDENT_TYPES.CROSS_RESPONDENT
        : PETITIONER_AND_RESPONDENT_TYPES.CROSS_DEFENDANT,
    ],
  ]);

  let respondingPartyType = "";
  if (party !== undefined) {
    for (const [key, value] of propoundingPartyNameMap) {
      const helper = new PropoundingPartyEnumHelper(key);
      if (helper.is(party)) {
        respondingPartyType = value;
        break;
      }
    }
    return respondingPartyType;
  }
  return party === PropoundingParty.Plaintiff
    ? `${isFamilyLaw ? PETITIONER_AND_RESPONDENT_TYPES.RESPONDENT : PETITIONER_AND_RESPONDENT_TYPES.DEFENDANT}${isCrossComplaint ? (isFamilyLaw ? `/${PETITIONER_AND_RESPONDENT_TYPES.CROSS_PETITIONER}` : `/${PETITIONER_AND_RESPONDENT_TYPES.CROSS_COMPLAINANT}`) : ""}`
    : `${isFamilyLaw ? PETITIONER_AND_RESPONDENT_TYPES.PETITIONER : PETITIONER_AND_RESPONDENT_TYPES.PLAINTIFF}${isCrossComplaint ? (isFamilyLaw ? `/${PETITIONER_AND_RESPONDENT_TYPES.CROSS_RESPONDENT}` : `/${PETITIONER_AND_RESPONDENT_TYPES.CROSS_DEFENDANT}`) : ""}`;
}

export class PropoundingPartyEnumHelper {
  role: number;

  constructor(role: PropoundingParty) {
    this.role = role;
  }
  getRole() {
    return this.role;
  }
  is(role: PropoundingParty) {
    return !!(this.role & role);
  }
}

/** RFP, RFA, special interrogatories — not FROG, complaint, or petition */
export function documentSubTypeSupportsSimilarResponses(
  documentSubType: DocumentSubType | undefined,
): boolean {
  if (documentSubType === undefined) {
    return false;
  }
  switch (documentSubType) {
    case DocumentSubType.SpecialInterrogatories:
    case DocumentSubType.RequestsForProduction:
    case DocumentSubType.RequestsForAdmission:
      return true;
    default:
      return false;
  }
}

function getClosestPartySubjectIsPlural(
  text: string,
  notPast: number,
  propoundingNames: string[],
  respondingNames: string[],
  propoundingPartyIsPlural: boolean,
  respondingPartyIsPlural: boolean,
): boolean {
  const searchArea = text.substring(0, notPast).toLowerCase();
  const lastIdx = (names: string[]) =>
    Math.max(
      -1,
      ...names
        .filter(Boolean)
        .map((n) => searchArea.lastIndexOf(n.toLowerCase())),
    );
  const pIdx = lastIdx(propoundingNames);
  const rIdx = lastIdx(respondingNames);
  if (pIdx < 0 && rIdx < 0) return respondingPartyIsPlural;
  if (pIdx > rIdx) return propoundingPartyIsPlural;
  return respondingPartyIsPlural;
}

export function replaceTokens(
  documentInfo: DocumentInfo | undefined,
  text: string,
  toUpperNonCustomRespondentNames: boolean,
  representative?: Representative,
  isFamilyLaw?: boolean,
  jurisdictionFriendlyName?: string | null,
) {
  if (!documentInfo) {
    return text;
  }

  const {
    propoundingPartyName,
    documentSubType,
    propoundingParty,
    isCrossComplaint,
    respondingParty,
    respondingPartyName,
    respondingPartyIsPlural,
    propoundingPartyIsPlural,
  } = documentInfo;

  const respondentDisplay = getRespondentDisplay(
    documentInfo,
    toUpperNonCustomRespondentNames,
    isFamilyLaw,
  );

  const request =
    documentSubType === DocumentSubType.SpecialInterrogatories ||
    documentSubType === DocumentSubType.FormInterrogatories
      ? "Interrogatory"
      : "Request";
  const requestObject =
    documentSubType === DocumentSubType.RequestsForProduction
      ? "documents"
      : "information";
  const requestObjectPlurality =
    documentSubType === DocumentSubType.RequestsForProduction ? "are" : "is";

  const repMembers = representative
    ? "Representative Members"
    : `${representative === Representative.ClassAction ? "CLASS MEMBERS" : "AGGRIEVED EMPLOYEES"}`;
  const repPeriod = representative
    ? "Representative Period"
    : `${representative === Representative.ClassAction ? "CLASS" : "PAGA"} PERIOD`;

  const plaintiffName =
    propoundingParty === PropoundingParty.Plaintiff
      ? propoundingPartyName
      : respondingParty === PropoundingParty.Plaintiff
        ? respondingPartyName
        : undefined;
  const defendantName =
    propoundingParty === PropoundingParty.Defendant
      ? propoundingPartyName
      : respondingParty === PropoundingParty.Defendant
        ? respondingPartyName
        : undefined;

  const propoundingTypeDisplay = getPartyDisplay(
    propoundingParty,
    isCrossComplaint,
    isFamilyLaw,
  );
  const respondingTypeDisplay = getPartyDisplay(
    respondingParty,
    isCrossComplaint,
    isFamilyLaw,
  );

  const propoundingNames = [
    propoundingPartyName,
    propoundingTypeDisplay,
    "{{ PROPOUNDING_PARTY_TYPE }}",
    "{{PROPOUNDING_PARTY_TYPE}}",
  ].filter(Boolean) as string[];
  const respondingNames = [
    respondingPartyName,
    respondingTypeDisplay,
    "{{ RESPONDING_PARTY_TYPE }}",
    "{{RESPONDING_PARTY_TYPE}}",
  ].filter(Boolean) as string[];

  const selectPluralityVerb = (
    offset: number,
    plural: string,
    singular: string,
  ) =>
    getClosestPartySubjectIsPlural(
      text,
      offset,
      propoundingNames,
      respondingNames,
      propoundingPartyIsPlural ?? false,
      respondingPartyIsPlural ?? false,
    )
      ? plural
      : singular;

  return text.replace(
    /\{\{ ?(\w+) ?\}\}/g,
    (match, placeholder: string, offset: number) => {
      switch (placeholder) {
        case "REQUEST":
          return request;
        case "REQUEST_OBJECT":
          return requestObject;
        case "REQUEST_TYPE":
          return getSubTypeText(
            documentSubType,
            documentInfo.jurisdiction ?? "",
          ).plural;
        case "REQUEST_TYPE_SINGULAR":
          return getSubTypeText(
            documentSubType,
            documentInfo.jurisdiction ?? "",
          ).singular;
        case "LINKING_VERB":
          return requestObjectPlurality;
        case "REPRESENTATIVE_MEMBERS":
          return repMembers;
        case "REPRESENTATIVE_PERIOD":
          return repPeriod;
        case "PROPOUNDING_PARTY":
          return propoundingPartyName ?? "Propounding Party";
        case "RESPONDING_PARTY":
          return respondingPartyName ?? "Responding Party";
        case "DEFENDANT_NAME":
          return defendantName ?? "{{DEFENDANT_NAME}}";
        case "PLAINTIFF_NAME":
          return plaintiffName ?? "{{PLAINTIFF_NAME}}";
        case "PROPOUNDING_PARTY_TYPE":
          return propoundingPartyIsPlural
            ? `${propoundingTypeDisplay}s`
            : propoundingTypeDisplay;
        case "PROPOUNDING_PARTY_TYPE_POSSESSIVE":
          return propoundingPartyIsPlural
            ? propoundingTypeDisplay + "s'"
            : propoundingTypeDisplay + "'s";
        case "RESPONDING_PARTY_TYPE":
          return respondingPartyIsPlural
            ? `${respondingTypeDisplay}s`
            : respondingTypeDisplay;
        case "RESPONDING_PARTY_TYPE_POSSESSIVE":
          return respondingPartyIsPlural
            ? respondingTypeDisplay + "s'"
            : respondingTypeDisplay + "'s";
        case "RESPONDENT":
          return respondentDisplay;
        case "HAS_HAVE":
          return selectPluralityVerb(offset, "have", "has");
        case "IS_ARE":
          return selectPluralityVerb(offset, "are", "is");
        case "WAS_WERE":
          return selectPluralityVerb(offset, "were", "was");
        case "DOES_DO":
          return selectPluralityVerb(offset, "do", "does");
        case "STATE":
          return jurisdictionFriendlyName ?? "State";
        default:
          return match;
      }
    },
  );
}

function formatAnswersForQuestions(
  questions: FormIntQuestion[],
  answers: string[],
  startAtAnswer: number,
) {
  return questions
    .map((question, index) => {
      const questionNumber = question.number ? `${question.number})` : "";
      const answer = answers[index + startAtAnswer] ?? "";
      return `${questionNumber} ${answer}<br />`;
    })
    .join("");
}

export function TryParseJsonToObject(text: string) {
  try {
    return JSON.parse(text || "[]") || [];
  } catch {
    return [];
  }
}

// Sentinel stored in additionalText when a user selects Yes/No on a conditional FROG
// but leaves the elaboration text empty. Matches ConditionalOnlyResponse's "yes" convention.
// Display logic must strip this value so the word "yes" is never rendered as response text.
export const FROG_CONDITIONAL_SENTINEL = "yes";

export function formatFormIntResponseForDisplay(
  text: string,
  formInt: FormInt | null,
) {
  if (!formInt) return "";

  // Strip sentinel so it never appears as elaboration text in output
  const displayText = text === FROG_CONDITIONAL_SENTINEL ? "" : text;

  let conditionalText = "";
  if (
    formInt?.conditionalAnswerDirection !== null &&
    formInt?.conditionalAnswerDirection !== undefined
  ) {
    if (formInt?.conditionalOnly) {
      conditionalText =
        formInt.conditionalAnswerDirection === true ? "Yes<br />" : "No<br />";
    } else {
      if (formInt.conditionalAnswerDirection === true) {
        conditionalText = text ? "Yes<br />" : "No<br />";
      } else {
        conditionalText = text ? "No<br />" : "Yes<br />";
      }
    }
  }
  try {
    let parsed: string[] | undefined;
    if (displayText) {
      parsed = JSON.parse(displayText);
    }
    if (parsed && Array.isArray(parsed)) {
      if (formInt?.canAddAdditional) {
        if (formInt.questions?.length) {
          // canAddAdditional MultiQuestion - response is array of arrays of strings
          const output: string[] = [];
          output.push(`<br />${conditionalText}`);
          const questions = formInt.questions || [];
          parsed.forEach((item: string, index: number) => {
            const answers: string[] = (item && JSON.parse(item)) || [];
            if (formInt.itemName) {
              let toAdd = "";
              if (parsed.length > 1) {
                const label =
                  answers[0] || `${formInt.itemName} ${(index + 1).toString()}`;
                toAdd = `<b>${label}</b><br />`;
              }
              output.push(
                `${toAdd}${formatAnswersForQuestions(questions, answers, 1)}`,
              );
            } else {
              output.push(formatAnswersForQuestions(questions, answers, 0));
            }
          });
          return output.join("<br />");
        }

        // canAddAdditional Simple - response is array of strings
        return `<br />${conditionalText}${parsed?.join("<br />")}`;
      }

      // MultiQuestion - response is array of strings
      if (formInt?.questions?.length) {
        const questions = formInt.questions || [];
        return `<br />${conditionalText}${parsed && formatAnswersForQuestions(questions, parsed as string[], 0)}`;
      }

      // not sure how/if we'd ever get here
      return parsed?.join("\n");
    }

    // Simple - response is just a string
    return `<br />${conditionalText}${parsed || displayText}`;
  } catch {
    return `<br />${conditionalText}${displayText}`;
  }
}

/** objections whose body text SRS may paste into when using a precedent response */
export function objectionIsSimilarRequestUseResponseTarget(
  objection: Objection | undefined,
): boolean {
  if (!objection) {
    return false;
  }
  if (
    objection.objectionType === ObjectionType.Response ||
    objection.objectionType === ObjectionType.NoResponse
  ) {
    return true;
  }
  if (objection.id === PRODUCE_DOCUMENTS_OBJECTION_ID) {
    return true;
  }
  const nameLower = objection.name?.trim().toLowerCase();
  return nameLower === "produce documents" || nameLower === "unable to produce";
}
